import { useRef } from "react";
import { motion, useInView } from "framer-motion";

function Reveal({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay: delay * 0.001, ease: [0.22, 1, 0.36, 1] }} className={className}>
      {children}
    </motion.div>
  );
}

export default function TermsPage() {
  return (
    <section className="pt-32 pb-24 md:pt-44 md:pb-32 px-6 lg:px-12" data-testid="terms-page">
      <div className="max-w-3xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E31212] mb-6">Legal</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mb-10"
            style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>
            Terms of Service
          </h1>
        </Reveal>

        <Reveal delay={100}>
          <div className="prose-invert space-y-8">
            <p className="text-sm text-[#6E7385]">Last updated: {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>1. Acceptance of Terms</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                By accessing or using Omni, a product of Masters Global Inc. ("Company"), you agree to be bound by these 
                Terms of Service ("Terms"). If you do not agree to these Terms, do not use the Service.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>2. Description of Service</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                Omni is an AI-powered digital companion that provides conversational support, wellness tracking, 
                goal management, and cultural wisdom through multiple specialized AI modules. The Service is 
                available as a web application, Progressive Web App (PWA), and native mobile application.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>3. Account Registration</h2>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li>You must provide accurate and complete registration information.</li>
                <li>You are responsible for maintaining the confidentiality of your account credentials.</li>
                <li>You must be at least 13 years old to create an account — date of birth is verified at signup and under-13 registration is blocked.</li>
                <li>Users aged 13–15 require verified parent/guardian consent: a consent link is emailed at signup and the account activates only after approval (UK, EU, US and Canada standard applied globally).</li>
                <li>All users under 18 automatically receive teen-safe content settings, regardless of jurisdiction.</li>
                <li>You are responsible for all activity under your account.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>4. Subscriptions & Payments</h2>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li>Free tier is available with limited features at no cost.</li>
                <li>Paid subscriptions (Omni+, Duo, Family, Platinum) are billed monthly or annually.</li>
                <li>All payments are processed securely through Stripe.</li>
                <li>You may cancel your subscription at any time. Access continues until the end of the current billing period.</li>
                <li>Refunds are handled in accordance with applicable consumer protection laws.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>5. Acceptable Use</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed mb-3">You agree not to:</p>
              <ul className="space-y-2 text-sm text-[#A1A5B3] leading-relaxed list-disc list-inside">
                <li>Use the Service to generate harmful, illegal, or abusive content.</li>
                <li>Attempt to reverse-engineer, hack, or compromise the Service.</li>
                <li>Share your account credentials with unauthorized users.</li>
                <li>Use automated tools or bots to access the Service without permission.</li>
                <li>Misrepresent Omni's AI responses as professional medical, legal, or financial advice.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>6. AI Disclaimer</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                <strong className="text-white">Omni is an AI companion and is not a substitute for professional advice.</strong> The 
                AI modules (including The Friend and The Mentor) provide general support and information. They 
                are not licensed therapists, counselors, medical professionals, or legal advisors. In any emergency or 
                crisis situation, please contact your local emergency services or a qualified professional immediately.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>7. Intellectual Property</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                The Omni name, logo, design, and all associated intellectual property are owned by Masters Global Inc. 
                Content generated by the AI in response to your inputs is provided for your personal use. You retain 
                ownership of any original content you provide to the Service.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>8. Family & Minor Accounts</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                Family plans allow parents and guardians to create accounts for minors (under 18). Parents accept full 
                responsibility for their children's use of the Service. Parental controls including "strict" (under 13) 
                and "teen-safe" (13-17) modes are enforced automatically based on the member's age.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>9. Limitation of Liability</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                To the maximum extent permitted by law, Masters Global Inc. shall not be liable for any indirect, 
                incidental, special, consequential, or punitive damages arising from your use of the Service. Our 
                total liability shall not exceed the amount you paid for the Service in the 12 months preceding the claim.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>10. Termination</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                We may suspend or terminate your account if you violate these Terms. You may delete your account at 
                any time through Settings. Upon termination, your data will be permanently deleted within 30 days, 
                except where retention is required by law.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>11. Governing Law</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                These Terms shall be governed by the laws applicable to Masters Global Inc.'s jurisdiction. Any disputes 
                shall be resolved through binding arbitration, except where prohibited by local law.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>12. Changes to Terms</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                We may update these Terms from time to time. We will notify you of material changes via email or 
                in-app notification. Continued use of the Service after changes constitutes acceptance.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-white mb-3" style={{ fontFamily: "'Century Gothic', 'Raleway', sans-serif", fontStyle: "italic" }}>13. Contact</h2>
              <p className="text-sm text-[#A1A5B3] leading-relaxed">
                For questions about these Terms, contact us at <a href="mailto:legal@omnimodules.com" className="text-[#E31212] hover:text-[#ff1e1e] transition-colors">legal@omnimodules.com</a>.<br />
                Masters Global Inc. — Global Operations
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
